import { css } from "@emotion/react"
import { Button, Modal, Thumbnail, Typography, useModal } from "matsuri-ui"
import OpenInFull from "@mui/icons-material/OpenInFull"
import CloseIcon from "@mui/icons-material/Close"
import { theme } from "@/theme"
import { Task } from "@/hooks/useCleaningTasks"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"

const ImageModal = ({ src, ...props }: { src: string }) => {
    return (
        <Modal
            {...props}
            body={
                <div
                    css={css`
                        display: flex;
                    `}
                >
                    <Thumbnail src={src} />
                </div>
            }
            header={<header />}
        />
    )
}

export const TaskListPage = ({
    tasks,
    cleaningId,
    handleTaskAssignmentPage
}: {
    tasks: Task[]
    cleaningId: string
    handleTaskAssignmentPage: () => void
}) => {
    const { t } = useTranslation()
    const [openModal] = useModal(ImageModal, {
        size: "lg",
        enableClickOutside: true
    })

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    return (
        <div
            css={css`
                padding-top: env(safe-area-inset-top, 0px);
                background: white;
            `}
        >
            <Header>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">
                            {t("清掃箇所一覧")}
                        </Typography>
                    }
                    onNext={openConfirmBack}
                    nextIcon={<CloseIcon />}
                />
            </Header>
            {/* 清掃詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="cleaning"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
            <ul
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                    text-decoration: none;
                    list-style: none;
                `}
            >
                {tasks?.map((task, index) => (
                    <li
                        key={task.id}
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                            width: 100%;
                            padding: 20px;
                            background: ${theme.palette.gray[200]};
                            border-radius: 8px;
                        `}
                    >
                        <Typography variant="h4" as="h2">
                            {`${index}. ${task.title}`}
                        </Typography>
                        <div>
                            <div
                                css={css`
                                    position: relative;
                                    width: 50%;
                                `}
                            >
                                <Thumbnail
                                    src={task.samplePictureUrl}
                                    aspectRatio={16 / 9}
                                />
                                <div
                                    css={css`
                                        position: absolute;
                                        right: 4px;
                                        bottom: 0px;
                                        z-index: 1;
                                    `}
                                >
                                    <Button
                                        icon={<OpenInFull />}
                                        color="default"
                                        variant="filled"
                                        size="small"
                                        onClick={() =>
                                            openModal({
                                                src: task.samplePictureUrl
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            css={css`
                                height: 0;
                                border-top: 1px solid rgb(0, 0, 0, 0.1);
                            `}
                        />
                        <Typography
                            variant="body"
                            css={css`
                                white-space: pre-wrap;
                            `}
                        >
                            {task.description}
                        </Typography>
                    </li>
                ))}
            </ul>
            <footer
                css={css`
                    position: sticky;
                    bottom: 0;
                    z-index: 999;
                    padding: 16px 16px 34px 16px;
                `}
            >
                <Button
                    variant="filled"
                    color="primary"
                    onClick={handleTaskAssignmentPage}
                    fullWidth
                >
                    {t("確認しました")}
                </Button>
            </footer>
        </div>
    )
}
